import "./copyButton.scss";
import { ComponentRegistry } from "@ts/utils/ComponentRegistry";
import { icon } from "@fortawesome/fontawesome-svg-core";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

type CopyButtonSuccessHook = (trigger: HTMLButtonElement | HTMLAnchorElement) => void | Promise<void>;
type CopyButtonFailHook = (error: unknown) => void | Promise<void>;

type CopyButtonOptions = {
    onSuccess?: CopyButtonSuccessHook;
    onFail?: CopyButtonFailHook;
    text?: string;
};

export class CopyButton {
    private readonly id: string;
    private static registry = new ComponentRegistry<CopyButton>("copy-button");
    private readonly trigger: HTMLButtonElement | HTMLAnchorElement;

    private readonly _config = {
        successDelay: 1000,
        copyBtnClass: "copy-btn"
    };

    private constructor(
        trigger: HTMLButtonElement | HTMLAnchorElement,
        private options: CopyButtonOptions
    ) {
        this.id = CopyButton.registry.register(trigger, this);
        this.trigger = trigger;
        this.trigger.classList.add(this._config.copyBtnClass);
        this.setClickEvent();
    }

    private setClickEvent() {
        this.trigger.addEventListener("click", async (e) => {
            e.preventDefault();

            try {
                const text = this.getCopyText();
                await this.copyToClipboard(text);

                if (typeof this.options.onSuccess === "function") {
                    await this.options.onSuccess(this.trigger);
                }

                this.setSuccessBtn();
            } catch (err: unknown) {
                if (typeof this.options.onFail === "function") {
                    await this.options.onFail(err);
                }
                this.setFailedBtn();
                console.log("Copy failed:", err);
            }
        });
    }

    private getCopyText(): string {
        const optionText = this.options.text?.trim();
        if (optionText) {
            return this.cleanText(optionText);
        }

        if (this.trigger instanceof HTMLAnchorElement) {
            const href = this.trigger.href?.trim();
            if (href) {
                return href;
            }
        }

        throw new Error("No copy text was provided.");
    }

    private setFailedBtn() {
        this.trigger.classList.add("copy-failed");
        const buttonLabel = this.trigger.querySelector(".btn-label");
        const originalLabelText = buttonLabel?.textContent ?? null;

        if (buttonLabel) {
            buttonLabel.textContent = "Error";
        }

        window.setTimeout(() => {
            this.trigger.classList.remove("copy-failed");

            if (buttonLabel && originalLabelText !== null) {
                buttonLabel.textContent = originalLabelText;
            }
        }, this._config.successDelay);
    }

    private setSuccessBtn() {
        this.trigger.classList.add("copy-success");
        const buttonLabel = this.trigger.querySelector(".btn-label");
        const originalLabelText = buttonLabel?.textContent ?? null;

        if (buttonLabel) {
            buttonLabel.textContent = "Copied";
        }

        const svgIcon = this.trigger.querySelector("svg");
        if (!svgIcon) {
            return;
        }

        const originalSvgClone = svgIcon.cloneNode(true) as SVGElement;
        const checkSvg = this.createIconSvg(faCircleCheck);
        svgIcon.replaceWith(checkSvg);

        window.setTimeout(() => {
            this.trigger.classList.remove("copy-success");

            if (buttonLabel && originalLabelText !== null) {
                buttonLabel.textContent = originalLabelText;
            }

            const currentSvg = this.trigger.querySelector("svg");
            if (currentSvg) {
                currentSvg.replaceWith(originalSvgClone);
            }
        }, this._config.successDelay);
    }

    private createIconSvg(iconDefinition: typeof faCircleCheck): SVGElement {
        const iconMarkup = icon(iconDefinition);
        const svgHtml = iconMarkup.html[0] ?? "";
        const fragment = document.createRange().createContextualFragment(svgHtml);
        const svg = fragment.firstElementChild as SVGElement;

        svg.setAttribute("aria-hidden", "true");

        return svg;
    }

    private async copyToClipboard(text: string) {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return;
        }

        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
    }

    private cleanText(text: string) {
        return text
            .replace(/\u00A0/g, " ")
            .replace(/\r\n/g, "\n")
            .replace(/[ \t]+/g, " ")
            .replace(/\n{2,}/g, "\n")
            .trim();
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: CopyButtonOptions = {}
    ): CopyButton[] | null {
        const triggers = ComponentRegistry.processTargets<CopyButton, HTMLElement>(
            CopyButton.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers.length) {
            return null;
        }

        return triggers.map((trigger) => new CopyButton(trigger, options));
    }

    public static getCopyButton(id: string): CopyButton | null {
        return CopyButton.registry.get(id) ?? null;
    }

    public destroy(): void {
        CopyButton.registry.unregister(this.id);
    }
}